import {
  ComparedValueEntity,
  DashboardEntity,
  DashboardOverviewEntity,
  UsersMetricsEntity,
  DashboardMediaTotalsEntity,
  DashboardEventsEntity,
  DashboardCommunityEntity,
  DashboardChatEntity,
  UserMetricPeriodEntity,
  PlanCountEntity
} from '../../data/entities/dashboard.entity'
import {
  ComparedValue,
  Dashboard,
  DashboardOverview,
  UserMetrics,
  DashboardMediaTotals,
  DashboardEvents,
  DashboardCommunity,
  DashboardChat,
  UserMetricPeriod,
  PlanCount
} from '../models/dashboard'

export class DashboardMapper {
  public map(entity: DashboardEntity): Dashboard {
    return {
      overview: this.mapOverview(entity.overview),
      userMetrics: this.mapUserMetrics(entity.user_metrics),
      mediaTotals: this.mapMediaTotals(entity.media_totals),
      events: this.mapEvents(entity.events),
      community: this.mapCommunity(entity.community),
      chat: this.mapChat(entity.chat)
    }
  }

  private mapOverview(entity: DashboardOverviewEntity): DashboardOverview {
    return {
      totalUsers: entity.total_users,
      paidUsers: entity.paid_users,
      freeUsers: entity.free_users,
      freeSubscribers: entity.free_subscribers,
      churnedUsers: entity.churned_users,
      incompleteSignups: entity.incomplete_signups,
      totalMembers: entity.total_members,
      activeMembers: entity.active_members,
      inactiveMembers: entity.inactive_members,
      paidPlans: (entity.paid_plans || []).map((p) => this.mapPlanCount(p))
    }
  }

  private mapUserMetrics(entity: UsersMetricsEntity): UserMetrics {
    return {
      uniqueVisitors: this.mapComparedValue(entity.unique),
      newSubscribedUsers: this.mapComparedValue(entity.new_subscribers),
      // Tolerate a backend that predates the plan-breakdown fields
      newFreeSubscribers: this.mapComparedValue(entity.new_free_subscribers || { current: 0, previous: 0, diff: 0 }),
      churnedUsers: this.mapComparedValue(entity.churned),
      newSubscribersByPlan: (entity.new_subscribers_by_plan || []).map((p) => this.mapPlanCount(p)),
      periods: entity.periods.map((p) => this.mapUserMetricPeriod(p))
    }
  }

  private mapUserMetricPeriod(entity: UserMetricPeriodEntity): UserMetricPeriod {
    return {
      date: entity.date,
      uniqueVisitors: entity.unique,
      newSubscribedUsers: entity.new_subscribers,
      newFreeSubscribers: entity.new_free_subscribers ?? 0,
      churnedUsers: entity.churned,
      annualSubscribers: entity.subscribers_yearly ?? 0,
      monthlySubscribers: entity.subscribers_monthly ?? 0
    }
  }

  private mapPlanCount(entity: PlanCountEntity): PlanCount {
    return {
      plan: entity.plan,
      count: entity.count
    }
  }

  private mapMediaTotals(entity: DashboardMediaTotalsEntity): DashboardMediaTotals {
    return {
      songsPlayed: this.mapComparedValue(entity.songs_played),
      videosViewed: this.mapComparedValue(entity.videos_viewed),
      mostPlayedSongs: (entity.most_played_songs || []).map((s) => ({
        id: s.id,
        name: s.name,
        value: s.value,
        diff: s.diff,
        imageUrl: s.image_url
      })),
      mostViewedVideos: (entity.most_viewed_videos || []).map((v) => ({
        id: v.id,
        name: v.name,
        value: v.value,
        diff: v.diff,
        imageUrl: v.image_url
      }))
    }
  }

  private mapEvents(entity: DashboardEventsEntity): DashboardEvents {
    return {
      buyNowClicks: this.mapComparedValue(entity.buy_now_clicks),
      presaleCodesRedeemed: this.mapComparedValue(entity.presale_codes_redeemed),
      meetAndGreetRedeemed: this.mapComparedValue(entity.meet_and_greet_redeemed),
      rsvpRedeemed: this.mapComparedValue(entity.rsvp_redeemed)
    }
  }

  private mapCommunity(entity: DashboardCommunityEntity): DashboardCommunity {
    return {
      viewedPosts: this.mapComparedValue(entity.viewed_posts),
      postCreated: this.mapComparedValue(entity.post_created),
      comments: this.mapComparedValue(entity.comments),
      upvotes: this.mapComparedValue(entity.upvotes)
    }
  }

  private mapChat(entity: DashboardChatEntity): DashboardChat {
    return {
      activeUsers: this.mapComparedValue(entity.active_users),
      messagesSent: this.mapComparedValue(entity.messages_sent),
      uniqueMessages: this.mapComparedValue(entity.unique_messages)
    }
  }

  private mapComparedValue(entity: ComparedValueEntity): ComparedValue {
    return {
      current: entity.current,
      previous: entity.previous,
      diff: entity.diff
    }
  }
}
